import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import NumberInput from "@/components/NumberInput";
import DateInput from "@/components/DateInput";

export default function OnboardingForm({ workerId: initialId, onDone }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [wid, setWid] = useState(initialId || "");
  const [w, setW] = useState(null);
  const [err, setErr] = useState(""); const [ok, setOk] = useState("");

  useEffect(() => {
    (async () => {
      if (!wid) {
        const { data } = await api.get("/me/onboarding");
        if (data.worker_id) setWid(data.worker_id);
      }
    })();
  }, [wid]);
  useEffect(() => { if (wid) api.get(`/workers/${wid}`).then(r=>setW(r.data)); }, [wid]);

  async function save(e) {
    e.preventDefault(); setErr(""); setOk("");
    try {
      const payload = { ...w, tax_code: w.tax_code || "M", employee_kiwisaver_pct: w.employee_kiwisaver_pct ?? 3 };
      // Workers can't send pay fields; strip
      if (user?.role === "Worker") { delete payload.pay_rate; delete payload.salary; delete payload.cost_rate_override; delete payload.charge_out_rate; }
      await api.patch(`/workers/${wid}`, payload);
      setOk("Saved");
      if (onDone) { onDone(); return; }
      if (user?.role === "Worker") {
        const { data } = await api.get("/me/onboarding");
        if (data.complete) nav("/my/timesheets", { replace: true });
        else setErr(`Still needed: ${data.missing.join(", ")}`);
      }
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
  }
  if (!w) return null;
  const isWorker = user?.role === "Worker";
  const form = (
    <form onSubmit={save} className="card space-y-3" data-testid="onboarding-form">
      <h2 className="text-silver">Worker onboarding</h2>
      {err && <div className="text-red-500 text-sm">{err}</div>}
      {ok && <div className="text-green-500 text-sm">{ok}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-sm">Full legal name<input className="input" value={w.name||""} onChange={e=>setW({...w,name:e.target.value})}/></label>
        <label className="text-sm">Email<input className="input" value={w.email||""} onChange={e=>setW({...w,email:e.target.value})}/></label>
        <label className="text-sm">Phone<input className="input" value={w.phone||""} onChange={e=>setW({...w,phone:e.target.value})}/></label>
        <label className="text-sm">IRD number (8–9 digits)<input className="input" value={w.ird_number||""} onChange={e=>setW({...w,ird_number:e.target.value})}/></label>
        <label className="text-sm">Tax code<select className="input" value={w.tax_code||"M"} onChange={e=>setW({...w,tax_code:e.target.value})}>
          {["M","M SL","ME","ME SL","SB","SB SL","S","S SL","SH","SH SL","ST","ST SL","CAE","EDW","NSW","WT"].map(c=><option key={c}>{c}</option>)}
        </select></label>
        <label className="text-sm">KiwiSaver %<select className="input" value={w.employee_kiwisaver_pct ?? 3} onChange={e=>setW({...w,employee_kiwisaver_pct:parseFloat(e.target.value)})}>
          {[3,4,6,8,10,0].map(n=><option key={n} value={n}>{n===0?"Not enrolled":`${n}%`}</option>)}
        </select></label>
        <label className="text-sm">Student loan<select className="input" value={w.student_loan ? "Yes":"No"} onChange={e=>setW({...w,student_loan:e.target.value==="Yes"})}>
          <option>No</option><option>Yes</option></select></label>
        <label className="text-sm">Bank account (XX-XXXX-XXXXXXX-XX or XX-XXXX-XXXXXXX-XXX)<input className="input" value={w.bank_account||""} onChange={e=>setW({...w,bank_account:e.target.value})}/></label>
        <label className="text-sm">Bank account name<input className="input" value={w.bank_account_name||""} onChange={e=>setW({...w,bank_account_name:e.target.value})}/></label>
        <label className="text-sm">Start date<DateInput className="input" value={(w.start_date||"").slice(0,10)} onChange={e=>setW({...w,start_date:e.target.value})}/></label>
        <label className="text-sm">Employment type<select className="input" value={w.employment_type||"Full time"} onChange={e=>setW({...w,employment_type:e.target.value})}>
          <option>Full time</option><option>Part time</option><option>Casual</option></select></label>
        {!isWorker && (
          <label className="text-sm">Agreed hourly rate ($/hr) — Admin only
            <NumberInput className="input" value={w.pay_rate||0} onChange={e=>setW({...w,pay_rate:parseFloat(e.target.value||0)})}/></label>
        )}
      </div>
      <h3 className="text-silver">Emergency contacts</h3>
      {[0,1].map(i => (
        <div key={i} className="grid grid-cols-3 gap-2">
          <input className="input" placeholder={`Contact ${i+1} name`} value={w.emergency_contacts?.[i]?.name||""} onChange={e=>{const ec=[...(w.emergency_contacts||[])]; ec[i]={...(ec[i]||{}),name:e.target.value}; setW({...w,emergency_contacts:ec});}}/>
          <input className="input" placeholder="Relationship" value={w.emergency_contacts?.[i]?.relationship||""} onChange={e=>{const ec=[...(w.emergency_contacts||[])]; ec[i]={...(ec[i]||{}),relationship:e.target.value}; setW({...w,emergency_contacts:ec});}}/>
          <input className="input" placeholder="Phone" value={w.emergency_contacts?.[i]?.phone||""} onChange={e=>{const ec=[...(w.emergency_contacts||[])]; ec[i]={...(ec[i]||{}),phone:e.target.value}; setW({...w,emergency_contacts:ec});}}/>
        </div>
      ))}
      <button className="btn-silver" data-testid="btn-save-onboarding">Save onboarding</button>
    </form>
  );
  if (initialId || onDone) return form;
  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-6 max-w-3xl mx-auto space-y-3" data-testid="onboarding-page">
      <div className="font-bold tracking-widest text-silver border border-silver px-3 py-1 inline-block">LW&amp;Co Hub</div>
      <p className="text-sm opacity-80">Welcome{user?.name ? `, ${user.name}` : ""}. Please complete your details before you start.</p>
      {form}
    </div>
  );
}
